import { cn } from "@gettactic/helpers";
import React from "react";
import {
  UnifiedSelect,
  SelectItem
} from "../UnifiedSelect";
import { FormControlWrapper } from "../FormControlWrapper";
import { FormControlOutline } from "../FormControlWrapper/FormControlOutline";
import { splitFormControlWrapperProps } from "../FormControlWrapper/helpers";
import {
  UnifiedSelectInputProps,
  UnifiedSelectInputPropsWithFormControl
} from "./UnifiedSelectInput.types";

const UnifiedSelectInputInput = <T = unknown>(
  props: UnifiedSelectInputProps<T>
) => {
  const {
    items,
    value,
    onChangeValue,
    values,
    onChangeValues,
    multiselect = false,
    inputClassName,
    placeholder = "Select...",
    disabled,
    ...rest
  } = props;

  // Convert string value to SelectItem
  const selectedItem = React.useMemo<SelectItem<T> | null>(() => {
    if (!value) return null;
    const findItem = (items: SelectItem<T>[]): SelectItem<T> | null => {
      for (const item of items) {
        if (item.id === value) return item;
        if (item.children) {
          const found = findItem(item.children);
          if (found) return found;
        }
      }
      return null;
    };
    return findItem(items);
  }, [items, value]);

  // Convert string array values to SelectItem array
  const selectedItems = React.useMemo<SelectItem<T>[]>(() => {
    if (!values || values.length === 0) return [];
    const findItems = (items: SelectItem<T>[]): SelectItem<T>[] => {
      const result: SelectItem<T>[] = [];
      for (const item of items) {
        if (values.includes(item.id)) {
          result.push(item);
        }
        if (item.children) {
          result.push(...findItems(item.children));
        }
      }
      return result;
    };
    return findItems(items);
  }, [items, values]);

  const handleSelect = (item: SelectItem<T> | null) => {
    onChangeValue?.(item?.id ?? "");
  };

  const handleSelectMultiple = (items: SelectItem<T>[]) => {
    onChangeValues?.(items.map((item) => item.id));
  };

  return (
    <>
      <UnifiedSelect<T>
        {...rest}
        items={items}
        value={selectedItem}
        values={selectedItems}
        onSelect={handleSelect}
        onSelectMultiple={handleSelectMultiple}
        placeholder={placeholder}
        disabled={disabled}
        multiselect={multiselect}
        triggerClassName={cn(
          "flex h-[42px] w-full min-w-0 items-center justify-between gap-1 cursor-pointer text-sm rounded-md border border-input bg-background px-3 py-2 hover:bg-accent/50",
          disabled &&
            "opacity-60 cursor-not-allowed !bg-secondary text-neutral-400 hover:bg-secondary",
          inputClassName
        )}
      />
    </>
  );
};

export const UnifiedSelectInput = React.forwardRef<
  React.ComponentRef<typeof FormControlWrapper>,
  UnifiedSelectInputPropsWithFormControl
>((props, ref) => {
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(props);
  const { disableOutline, ...restInputProps } = inputProps;

  return (
    <FormControlWrapper
      variant={"unstyled"}
      wrapperClassName={"py-0"}
      enableSlotsInputFocus
      ref={ref}
      {...wrapperProps}
      isDisabled={wrapperProps.isDisabled || restInputProps.disabled}
    >
      <UnifiedSelectInputInput {...restInputProps} />
      {disableOutline !== true && <FormControlOutline />}
    </FormControlWrapper>
  );
}) as unknown as (<T = unknown>(
  props: UnifiedSelectInputPropsWithFormControl<T> & {
    ref?: React.Ref<React.ComponentRef<typeof FormControlWrapper>>;
  }
) => React.ReactElement) & { displayName?: string };

UnifiedSelectInput.displayName = "UnifiedSelectInput";
